import { useFormik } from 'formik';
import { validatePhoneNumberLength } from 'libphonenumber-js';
import { useEffect, useMemo, useState } from 'react';
import { getPhoneNumberByEmail } from 'src/actions';
import { ChallengeType, LoginMethod, PhoneUpdateStatus } from 'src/lib/enum';

import { CognitoUser } from '@aws-amplify/auth';

import { isEmailValid, resendCode, signIn, verifyCode } from '../../lib/auth/helpers';
import {
  trackOTPResend, trackPhoneNumberEdit, trackSignInAttempt, trackSignInError, trackSignInOTPSent,
  trackSignInSuccess
} from '../../lib/mixpanel';
import {
  setLoginMethod, setPhoneUpdateStatus as setPhoneUpdateStatusLocalStorage
} from '../../lib/utils';

interface SignInValues {
  username: string;
  code: string;
  codeSent: boolean;
}

let isRequestingOTP = false;
let cognitoUser: CognitoUser | null;

const isPhoneNumber = (value: string) => value.includes('+') || /^\d+$/.test(value);

export default (loginMethod: LoginMethod = LoginMethod.PHONE) => {
  const [resendIn, setResendIn] = useState<null | number>(null);
  const [loading, setLoading] = useState<boolean | undefined>();
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [retryCountdown, setRetryCountdown] = useState<number | null>(null);
  const [phoneUpdateStatus, setPhoneUpdateStatus] = useState<PhoneUpdateStatus | null>(null);

  const handleResend = async (phoneNumber: string) => {
    const isEmail = loginMethod === LoginMethod.EMAIL;
    const data = cognitoUser && (await resendCode(cognitoUser, isEmail));
    if (data) {
      setResendIn(data.resendIn);
      trackOTPResend(phoneNumber);
    }
  };

  const handleEditPhone = (phoneNumber: string) => {
    setIsEditingPhone(true);
    form.setFieldValue('codeSent', false);
    form.setFieldValue('code', '');
    cognitoUser = null;
    setResendIn(null);
    isRequestingOTP = false;
    trackPhoneNumberEdit(phoneNumber);
  };

  useEffect(() => {
    if (!resendIn) {
      return;
    }

    const timer = setInterval(() => {
      if (resendIn - 1 === 0) {
        setResendIn(() => null);
      } else {
        setResendIn((prevResend) => prevResend && prevResend - 1);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [resendIn]);

  useEffect(() => {
    if (!retryCountdown) {
      form.setFieldError('username', undefined as any);
      return;
    }

    const timer = setInterval(() => {
      if (retryCountdown - 1 === 0) {
        setRetryCountdown(null);
      } else {
        setRetryCountdown((prevCountdown) => {
          const newCount = prevCountdown && prevCountdown - 1;
          return newCount;
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [retryCountdown]);

  const sendSignInOTP = async (username: string) => {
    if (!isRequestingOTP) {
      isRequestingOTP = true;
      const isEmail = loginMethod === LoginMethod.EMAIL;

      try {
        let phoneNumber = username;

        if (isEmail) {
          const { phoneNumber: newPhoneNumber, phoneUpdateStatus } = await getPhoneNumberByEmail(username);
          phoneNumber = newPhoneNumber;
          setPhoneUpdateStatus(phoneUpdateStatus);

          if (phoneUpdateStatus === PhoneUpdateStatus.COMPLETED) {
            throw new Error('PhoneUpdateCompletedError');
          }
        }

        const challengeType = isEmail ? ChallengeType.EMAIL_OTP : ChallengeType.OTP;
        const data = await signIn(phoneNumber, challengeType, isEmail);
        if (data && !resendIn) {
          setResendIn(data.resendIn);
        }
        cognitoUser = data.cognitoUser;
        trackSignInOTPSent(username, undefined, loginMethod);
        return true;
      } catch (error: any) {
        isRequestingOTP = false;
        throw error;
      }
    }
    return false;
  };

  const form = useFormik<SignInValues>({
    initialValues: {
      username: '',
      code: '',
      codeSent: false
    },
    validateOnMount: true,

    validate: (values: SignInValues) => {
      const errors: any = {};

      if (!values.username) {
        errors.username = 'This field is required';
        return errors;
      }

      if (isPhoneNumber(values.username)) {
        if (validatePhoneNumberLength(values.username)) {
          errors.username = 'Please enter a valid phone number';
        }
      } else {
        if (!isEmailValid(values.username)) {
          errors.username = 'Please enter a valid email address';
        }
      }

      return errors;
    },
    onSubmit: async (values, { setFieldValue, setSubmitting, setFieldError, setErrors }) => {
      try {
        setLoading(true);

        if (values.codeSent) {
          const isEmail = loginMethod === LoginMethod.EMAIL;
          const challengeType = isEmail ? ChallengeType.EMAIL_OTP : ChallengeType.OTP;
          cognitoUser && (await verifyCode(values.code, cognitoUser, challengeType, isEmail));
          cognitoUser = null;
          trackSignInSuccess(values.username, undefined, loginMethod);

          setLoginMethod(loginMethod);

          if (phoneUpdateStatus === PhoneUpdateStatus.PENDING) {
            setPhoneUpdateStatusLocalStorage(PhoneUpdateStatus.PENDING);
          }
        } else {
          trackSignInAttempt(values.username, undefined, loginMethod);

          if (await sendSignInOTP(values.username)) {
            isRequestingOTP = false;
            setFieldValue('codeSent', true);
            setIsEditingPhone(false);
            setLoading(false);
          }
        }
      } catch (error: any) {
        console.debug({ error });
        setSubmitting(false);
        setLoading(false);
        isRequestingOTP = false;

        const isGetPhoneNumberError = ['EmailRequiredError', 'UserNotFoundError', 'NotAffiliateError', 'Network error', 'PhoneUpdateCompletedError'].includes(error.message);
        setRetryCountdown(isGetPhoneNumberError ? 10 : 30);
        setErrors({});

        let errorType = 'unknown';
        let errorMessage = error.message || 'Unknown error';

        switch (error.message) {
          case 'INVALID_CODE':
            setFieldError('code', 'Invalid OTP code');
            errorType = 'invalid_code';
            errorMessage = 'Invalid OTP code';
            break;
          case 'CAPTCHA_ERROR':
            setFieldError('username', 'reCAPTCHA verification failed. Please try again.');
            errorType = 'captcha_error';
            errorMessage = 'reCAPTCHA verification failed';
            break;
          case 'Network error':
            setFieldError('username', 'Something went wrong. Please try again later');
            errorType = 'network_error';
            errorMessage = 'Network error occurred';
            break;
          case 'EmailRequiredError':
            setFieldError('username', 'Email parameter is required');
            errorType = 'email_required';
            errorMessage = 'Email parameter is required';
            break;
          case 'UserNotFoundError':
            setFieldError('username', 'No user found with the provided email address');
            errorType = 'user_not_found';
            errorMessage = 'No user found with the provided email address';
            break;
          case 'NotAffiliateError':
            setFieldError('username', 'This email is not associated with an affiliate account');
            errorType = 'not_affiliate';
            errorMessage = 'This email is not associated with an affiliate account';
            break;
          case 'PhoneUpdateCompletedError':
            setFieldError('username', 'Please log in using your phone number');
            errorType = 'phone_update_completed';
            errorMessage = 'Please log in using your phone number';
            break;
          default:
            if (error.code === 'UserNotFoundException') {
              const isEmail = isEmailValid(values.username);
              const fieldName = isEmail ? LoginMethod.EMAIL : LoginMethod.PHONE;
              setFieldError('username', `${fieldName} not found`);
              errorType = 'user_not_found';
              errorMessage = `${fieldName} not found`;
            } else if (error.code === 'NotAuthorizedException') {
              setFieldError('username', 'Please wait to retry OTP');
              errorType = 'not_authorized';
              errorMessage = 'Please wait to retry OTP';
            } else {
              setFieldError('username', error.message);
              errorType = 'other';
              errorMessage = error.message;
            }
        }

        trackSignInError(values.username, errorType, errorMessage, undefined, loginMethod);
      }
    }
  });

  const resendInPadded = useMemo(
    () =>
      resendIn &&
      `${Math.floor(resendIn / 60)
        .toString()
        .padStart(2, '0')}:${(resendIn % 60).toString().padStart(2, '0')}`,
    [resendIn]
  );

  const retryCountdownPadded = useMemo(
    () =>
      retryCountdown &&
      `${Math.floor(retryCountdown / 60)
        .toString()
        .padStart(2, '0')}:${(retryCountdown % 60).toString().padStart(2, '0')}`,
    [retryCountdown]
  );

  return {
    handleResend,
    handleEditPhone,
    isEditingPhone,
    resendIn: resendInPadded,
    retryCountdown: retryCountdownPadded,
    loading,
    ...form
  };
};
